import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import ReactGA4 from 'react-ga4';
import type { DeepReadonly } from 'ts-essentials';

declare global {
  interface Window {
    ga?: UniversalAnalytics.ga;
    JANE_GOOGLE_ANALYTICS_ENHANCED_ECOMMERCE_ENABLED: boolean;
  }
}

type OpenModalParams =
  | { goBack?: () => void; isCheckoutAlert?: boolean; name: 'cartAlerts' }
  | { name: 'login' }
  | { name: 'logout' }
  | { name: 'replaceCart' }
  | {
      id: number | string;
      isMember: boolean;
      name: 'toggleCustomersMembership';
    };

type OpenModalPayload = {
  disallowClose?: boolean;
} & OpenModalParams;

type ApplicationState = DeepReadonly<{
  googleAnalyticsInstance: string | null;
  isModalOpen: boolean;
  janeDeviceId: string | undefined;
  mixpanelDistinctId: string | undefined;
  modalName: string;
  modalOptions: Omit<OpenModalPayload, 'name'>;
  notificationShown: boolean;
}>;

const getInitialState = (): ApplicationState => ({
  isModalOpen: false,
  modalName: '',
  modalOptions: {},
  notificationShown: false,
  janeDeviceId: undefined,
  mixpanelDistinctId: undefined,
  googleAnalyticsInstance: null,
});

const initializeEcommerceGa = function (trackingId: string, referrer?: string) {
  if (trackingId && trackingId.startsWith('UA')) return;

  ReactGA4.initialize(trackingId, {
    testMode: process.env['NODE_ENV'] === 'test',
    gaOptions: {
      cookieFlags: 'SameSite=None; Secure',
      cookieDomain: 'auto',
    },
  });

  try {
    const trackers = window?.ga?.getAll() || [];
    trackers.forEach((tracker: UniversalAnalytics.Tracker) =>
      tracker.set('referrer', referrer)
    );
  } catch {
    // quietly catch
  } finally {
    if (referrer) {
      Object.defineProperty(document, 'referrer', {
        get: () => referrer,
      });
    }
  }
};

const removeGa = function () {
  if (window.ga) {
    window.ga('remove');
  }
};

const applicationSlice = createSlice({
  name: 'application',
  initialState: getInitialState(),
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalPayload>) => {
      const { name, ...rest } = action.payload;
      return {
        ...state,
        isModalOpen: true,
        modalName: name,
        modalOptions: rest,
      };
    },
    closeModal: (state) => {
      return { ...state, isModalOpen: false, modalName: '', modalOptions: {} };
    },
    setPreviousPath: (state, _action: PayloadAction<string>) => {
      const { janeDeviceId, mixpanelDistinctId } = state;
      return {
        ...getInitialState(),
        janeDeviceId,
        mixpanelDistinctId,
      };
    },
    setJaneDeviceId: (
      state,
      action: PayloadAction<{ janeDeviceId: string }>
    ) => {
      const { janeDeviceId } = action.payload;
      return {
        ...state,
        janeDeviceId,
      };
    },
    setMixpanelDistinctId: (
      state,
      action: PayloadAction<{ mixpanelDistinctId: string }>
    ) => {
      const { mixpanelDistinctId } = action.payload;
      return {
        ...state,
        mixpanelDistinctId,
      };
    },
    addGoogleAnalyticsInstance: (
      state,
      action: PayloadAction<{ trackingId: string }>
    ) => {
      const { trackingId } = action.payload;
      if (
        !state.googleAnalyticsInstance ||
        state.googleAnalyticsInstance !== trackingId
      ) {
        Object.defineProperty(
          window,
          'JANE_GOOGLE_ANALYTICS_ENHANCED_ECOMMERCE_ENABLED',
          { value: true, writable: false }
        );

        initializeEcommerceGa(trackingId);
        window.addEventListener('message', function (event) {
          /// expects message as follows
          /// frameWindow.postMessage({ messageType: 'initializeGa', payload: { clientId: '123' }}, "*")
          if (
            event.data &&
            event.data.messageType === 'initializeGa' &&
            event.data.payload?.clientId
          ) {
            removeGa();
            initializeEcommerceGa(trackingId, event.data.payload?.referrer);
          }
        });

        return {
          ...state,
          googleAnalyticsInstance: trackingId,
        };
      }

      return state;
    },
  },
  selectors: {
    selectApplication: (state) => state,
  },
});

export const {
  reducer: applicationReducer,
  actions: {
    addGoogleAnalyticsInstance,
    closeModal,
    openModal,
    setJaneDeviceId,
    setMixpanelDistinctId,
    setPreviousPath,
  },
  selectors: { selectApplication },
} = applicationSlice;

export type ApplicationActions =
  | ReturnType<typeof addGoogleAnalyticsInstance>
  | ReturnType<typeof closeModal>
  | ReturnType<typeof openModal>
  | ReturnType<typeof setJaneDeviceId>
  | ReturnType<typeof setMixpanelDistinctId>
  | ReturnType<typeof setPreviousPath>;
